import type { StyleRule } from '@archipro-design/aria';
import {
    labelClassName,
    profileNavigationClassNames,
    profileNavigationItemClassName,
    pxArrayToRem,
    pxToRem,
    dropdownSlotClassNames,
} from '@archipro-design/aria';

import type { MobileSubCategoryNavigationState } from '@modules/articles/component/category-navigation/CategoryNavigation.mobile';
import { buttonContentClassName } from '@fluentui/react-northstar';

const BaseTextStyle: StyleRule = ({ theme }) => ({
    letterSpacing: theme.siteVariables.letterSpacingMedium,
    lineHeight: theme.siteVariables.lineHeightSmallest,
    fontSize: theme.siteVariables.fontSizes['caption03'],
    color: theme.siteVariables.colors.charcoal['300'],
    ...theme.siteVariables.textStyles.Text.Roman55,
});

export const NavLink: StyleRule = ({ theme }) => ({
    ...BaseTextStyle({ theme }),
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    '&:visited, &:link, &:hover, &:active': {
        color: theme.siteVariables.colors.charcoal['300'],
        textDecoration: 'none',
    },
});

export const CloseOverlay: StyleRule = ({ theme }) => ({
    zIndex: theme.siteVariables.zIndexes.overlay,
    position: 'fixed',
    top: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
});

export const ToggleChevron: StyleRule<{
    toggleState: MobileSubCategoryNavigationState;
}> = ({ toggleState }) => ({
    ...(toggleState.current === 'opened' && { transform: 'rotate(180deg)' }),
});

export const DropdownRoot: StyleRule<{
    toggleState: MobileSubCategoryNavigationState;
}> = ({ theme, toggleState }) => ({
    display: 'flex',
    justifyContent: 'center',
    height: pxToRem(54),
    width: '100%',
    marginBottom: pxToRem(16),

    [`& .${dropdownSlotClassNames.container}`]: {
        marginLeft: pxToRem(18),
        marginRight: pxToRem(18),

        [`& .${dropdownSlotClassNames.toggleIndicator}`]: {
            right: `${pxToRem(13)} `,
            marginTop: `${pxToRem(2)}`,
        },

        [`& .${dropdownSlotClassNames.itemsList}`]: {
            paddingTop: 0,
            paddingBottom: 0,
            zIndex: theme.siteVariables.zIndexes.overlayPriority,

            '&:last-child': {
                borderBottom: `none`,
            },

            [`& .${dropdownSlotClassNames.item}`]: {
                padding: pxArrayToRem([0, 12]),
                ...BaseTextStyle({ theme }),
                border: 'none',
                borderTop: `1px solid ${theme.siteVariables.colors.transparency.black['6%']}`,

                '&:first-child': {
                    borderTop: 'none',
                },
            },
        },

        [`& .${dropdownSlotClassNames.selectedItems}`]: {
            maxHeight: pxToRem(54),
            height: pxToRem(54),
            overflowY: 'hidden',
            borderColor: `${theme.siteVariables.colors.transparency.black['6%']}`,
            zIndex: theme.siteVariables.zIndexes.overlayPriority + 1,
        },
    },

    ...(toggleState.current === 'closed' && {
        [`& .${dropdownSlotClassNames.selectedItems}`]: {
            border: `solid ${theme.siteVariables.colors.transparency.black['6%']}`,
            borderWidth: '1px 0 !important',
        },
    }),

    ...(toggleState.current === 'opened' && {
        [`& .${dropdownSlotClassNames.selectedItems}`]: {
            borderWidth: '0 1px',
            borderBottom: `1px solid ${theme.siteVariables.colors.transparency.black['6%']}`,
        },
    }),
});

export const DropdownTrigger: StyleRule<{
    toggleState: MobileSubCategoryNavigationState;
}> = ({ theme, toggleState }) => ({
    height: pxToRem(54),
    width: '100%',

    [`& .${buttonContentClassName}`]: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignContent: 'flex-start',
        flexWrap: 'wrap',
        width: '100%',
        height: ' 100%',
        ...BaseTextStyle({ theme }),
        ...(toggleState.current === 'opened' &&
            theme.siteVariables.textStyles.Text.Medium65),
    },
});
export const DropdownItemSizing: StyleRule = () => ({
    height: pxToRem(54),
    width: '100%',
    padding: pxArrayToRem([0, 14]),
});
export const DropdownItem: StyleRule = ({ theme }) => ({
    ...DropdownItemSizing({ theme }),
    display: 'flex',
    alignItems: 'center',
    lineHeight: '100%',
    fontSize: pxToRem(14),
    background: theme.siteVariables.colors.white['FFF'],

    [`& .${dropdownSlotClassNames.item}`]: {
        background: theme.siteVariables.colors.white['FFF'],
    },
});

export const MobileNav: StyleRule = ({ theme }) => ({
    position: 'relative',
    marginBottom: pxToRem(15),
    display: 'flex',
    flexDirection: 'column',

    [`& .${profileNavigationClassNames.container}`]: {
        padding: 0,

        '::-webkit-scrollbar': {
            display: 'none',
        },
        [`& .${profileNavigationClassNames.itemContainer}`]: {
            padding: pxArrayToRem([0, 18]),
        },

        [`& .${profileNavigationItemClassName}`]: {
            height: pxToRem(47),
            boxShadow: `inset 0px 0px 0px 1px ${theme.siteVariables.colors.transparency.black['10%']}`,
            marginBottom: pxToRem(1),
            border: 'none',
            [`&:not(.active)`]: {
                background: 'transparent',
                color:
                    theme.siteVariables.colorScheme.default?.foreground ??
                    theme.siteVariables.bodyColor,
            },
            [`& .${labelClassName}`]: {
                whiteSpace: 'nowrap',
            },
            '&.active': {
                background: theme.siteVariables.colors.charcoal['000'],
                color: theme.siteVariables.colors.white['000'],
            },
            '&:active': {
                border: 'inherit',
            },
        },
    },
});
